// Angular modules.
import {
	AfterViewInit,
	Component,
	EventEmitter,
	OnInit,
	Output,
	ViewChildren,
} from '@angular/core';
import { AngularFireAuth } from '@angular/fire/auth';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
// Third-party modules.
import { TranslateService } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular-ivy';
import { ApiErrorResponse } from 'app/interfaces/error.interface';
import { IProfessionalProfile } from 'app/interfaces/professionalprofile.interface';
// Zenklub services.
import { AuthenticationService } from 'app/services/authentication.service';
import { ChatService } from 'app/services/chat.service';
import { DateTimeService } from 'app/services/datetime.service';
import { ErrorService } from 'app/services/error.service';
import { GlobalService } from 'app/services/global.service';
import { NetworkService } from 'app/services/network.service';
import { UserService } from 'app/services/user.service';
import { ZkuService } from 'app/services/zku.service';
import { enterAnimation, loadingAnimation } from 'app/utils/animations.util';
import firebase from 'firebase';
import { first } from 'rxjs/operators';

@Component({
	selector: 'app-admin-login',
	templateUrl: './admin.component.html',
	styleUrls: ['./admin.component.scss'],
	animations: [enterAnimation, loadingAnimation],
})
export class AdminLoginComponent implements OnInit, AfterViewInit {
	@Output() testX: EventEmitter<string> = new EventEmitter<string>();
	@ViewChildren('inputEmail') emailField: {
		first: { nativeElement: { focus: () => void } };
	};
	@ViewChildren('inputPassword') passField: {
		first: { nativeElement: { focus: () => void } };
	};
	@ViewChildren('inputProfessionalId') professionalIdField: {
		first: { nativeElement: { focus: () => void } };
	};
	loading = false;
	starting = true;
	justSignedUp: boolean;
	error = '';
	// Status.
	currentOp = 'Teste';
	statusLoader: string;
	userIsLoggedIn = false;
	model: any = {};
	email = '';
	password = '';
	professionalId = '';
	state = '';
	paddingTop: number;
	// Translated strings.
	strPreparing: string;
	strStarting: string;
	strStatusTrying: string;
	strStatusChat: string;
	strStatusCheck: string;
	strStatusData: string;
	strStatusStarting: string;
	strStatusUpdating: string;

	// Constructor.
	constructor(
		private afAuth: AngularFireAuth,
		protected authenticationService: AuthenticationService,
		protected dateTime: DateTimeService,
		protected errorService: ErrorService,
		protected global: GlobalService,
		protected network: NetworkService,
		protected router: Router,
		protected translate: TranslateService,
		protected userService: UserService,
		protected chatService: ChatService,
		private zkuService: ZkuService,
		private activatedRoute: ActivatedRoute
	) {
		if (this.activatedRoute.snapshot.queryParams['accountCreated'] === 'true') {
			this.justSignedUp = true;
		}

		// override the route reuse strategy
		this.router.routeReuseStrategy.shouldReuseRoute = function () {
			return false;
		};
		this.router.events.subscribe((evt) => {
			if (evt instanceof NavigationEnd) {
				// trick the Router into believing it's last link wasn't previously loaded
				this.router.navigated = false;
			}
		});
		// Get translated texts.
		this.translate
			.get([
				'GENERAL.PREPARING',
				'GENERAL.STARTING',
				'LOGIN.STATUS.CHAT',
				'LOGIN.STATUS.CHECKDATA',
				'LOGIN.STATUS.TRYING',
				'LOGIN.STATUS.GETDATA',
				'LOGIN.STATUS.STARTINGAPP',
				'LOGIN.STATUS.UPDATELOCALDATA',
			])
			.subscribe((res) => {
				// Strings.
				this.strPreparing = res['GENERAL.PREPARING'];
				this.strStarting = res['GENERAL.STARTING'];
				this.strStatusChat = res['LOGIN.STATUS.CHAT'];
				this.strStatusCheck = res['LOGIN.STATUS.CHECKDATA'];
				this.strStatusTrying = res['LOGIN.STATUS.TRYING'];
				this.strStatusData = res['LOGIN.STATUS.GETDATA'];
				this.strStatusStarting = res['LOGIN.STATUS.STARTINGAPP'];
				this.strStatusUpdating = res['LOGIN.STATUS.UPDATELOCALDATA'];

				// Loader status.
				this.statusLoader = this.strStarting;
			});
	}

	// On init.
	public ngOnInit() {
		this.afAuth.onAuthStateChanged((user: firebase.User) => {
			if (user && !this.loading) {
				this.redirectToApp();
			}
		});

		// Loader status.
		this.statusLoader = this.strPreparing || 'Zenklub Office';

		// Continue.
		this.statusLoader = this.strStarting;
		// Started.
		setTimeout(() => {
			this.starting = false;
			setTimeout(() => {
				// Set focus on first empty element.
				if (this.emailField.first) {
					this.emailField.first.nativeElement.focus();
				} else if (this.passField.first) {
					this.passField.first.nativeElement.focus();
				}
			}, 500);
		}, 1500);
	}

	// After view init.
	public ngAfterViewInit() {
		this.state = 'in';
	}

	// Try login.
	login() {
		// Show loader.
		this.loading = true;
		// Current status.
		this.currentOp = this.strStatusTrying;
		// Remove spaces.
		this.email = this.email.trim();

		// Try to log in.
		this.authenticationService
			.adminLogin(this.email, this.password, this.professionalId)
			.pipe(first())
			.subscribe(
				(tokenResponse) => {
					if (!tokenResponse.professional) {
						throw { type: 'NO_ACCESS' };
					}
					this.afAuth
						.signInWithCustomToken(tokenResponse.token)
						.then((result) => {
							return result.user.getIdTokenResult();
						})
						.then((tokenData) => {
							// if it is not a professional reject it
							if (!tokenData || !tokenData.claims.professional) {
								throw { type: 'HTTP.NO_ACCESS' };
							}

							// Current status.
							this.currentOp = this.strStatusData;
							const uid = firebase.auth().currentUser.uid;

							Sentry.configureScope((scope: any) => {
								scope.setUser({
									id: uid,
									email: this.email,
								});
							});

							// Get user's data.
							this.userService
								.getProfessionalProfileData(uid)
								.subscribe((userData: IProfessionalProfile) => {
									if (userData) {
										const uImage =
											userData.imageUrl || this.global.getDefaultUserPicture();

										this.zkuService.setZku({
											...userData,
											imageUrl: uImage,
											profile: userData.profileUrl,
											name: userData.displayName,
											timezone: userData.timezone
												? userData.timezone
												: this.dateTime.getTimezoneName(),
										});

										// Current status.
										this.currentOp = this.strStatusUpdating;
										// Update local data.
										this.network.updateLocalData().then(
											() => {
												// Current status.
												this.currentOp = this.strStatusChat;
												// Current status.
												this.currentOp = this.strStatusStarting;
												// Redirect to application.
												this.redirectToApp();
											},
											() => {
												throw { type: 'HTTP.UPDATELOCALDATA' };
											}
										);
									} else {
										// throw error
										throw new ApiErrorResponse({
											error: {
												code: 'AUTH/INVALID-CREDENTIALS',
											},
										});
									}
								});
						})
						.catch((error: any) => {
							const aerror = new ApiErrorResponse({ error });
							if (aerror.isApiError() || aerror.isFirebaseError()) {
								this.error = this.errorService.getMessage(aerror.error);
							} else if (error.type) {
								this.error = this.errorService.getMessage(error.type);
							}

							this.userService.setOnlineStatus(false).subscribe(() => {
								this.authenticationService.logoutServices();
							});

							this.loading = false;
						});
				},
				(error: ApiErrorResponse) => {
					this.error = this.errorService.getMessage(error.error);
					this.loading = false;
				}
			);
	}

	// Redirect to app.
	protected redirectToApp(): void {
		// we just logged in, we need to init chat,
		// since the chat init code on app.component.ts has already run and
		// was ignored because we were not logged in
		this.chatService.initChat();
		// Get user's data.
		this.currentOp = this.strStatusCheck;
		// Loaded.
		const userDt = this.userService.getUserLocal();
		if (
			userDt &&
			(!userDt.timezone || userDt.timezone.indexOf('undefined') > -1)
		) {
			this.router.navigateByUrl('/account/definitions');
		} else {
			// Redirect to home page.
			this.router.navigateByUrl('/calendar');
		}
	}
}
